/**
 * RealRoomingOrchestrator
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode twin of `RoomingOrchestrator` (AISchedulerBundle). Same
 * visual structure — header KPIs, "Live re-plan" badge, swap proposal
 * rows with One-click apply / Preview / Dismiss CTAs — but ZERO demo
 * swap fixtures. Pulls suggestions from
 *   GET  /api/scheduler/rooming/swaps?location_id=…&date=…
 *   POST /api/scheduler/rooming/swaps/{id}/apply
 *   POST /api/scheduler/rooming/swaps/{id}/dismiss
 *
 * Endpoint-missing path (404/501) renders the structured empty state
 * with the EXACT endpoint contract for backend follow-up.
 *
 * Surfaces a single `data-rooming-source` attribute on the root so QA
 * can grep one attribute to know whether the strip is loading / live /
 * empty / api_missing / error — same pattern as RealCampaignRecallsPanel.
 */
import { useEffect, useState, useCallback } from 'react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Sparkles, Users, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { get as httpGet, post as httpPost } from '../../../../lib/api/httpClient';

const RealRoomingOrchestrator = ({ locationId, date, allowApply = true, onActionDone }) => {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiMissing, setApiMissing] = useState(false);
  const [appliedIds, setAppliedIds] = useState(() => new Set());
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  const refetch = useCallback(async () => {
    if (!locationId) return;
    setLoading(true); setError(null);
    const res = await httpGet('/api/scheduler/rooming/swaps', { location_id: locationId, date });
    if (res?.ok) {
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.swaps || res.data?.results || res.data?.items || []);
      setSwaps(list); setApiMissing(false);
    } else {
      const status = res?.error?.status ?? null;
      setSwaps([]);
      if (status === 404 || status === 501) { setApiMissing(true); setError(null); }
      else { setApiMissing(false); setError(res?.error || { message: 'Failed to load swaps' }); }
    }
    setLoading(false);
  }, [locationId, date]);

  useEffect(() => { refetch(); }, [refetch]);

  const applySwap = async (s) => {
    if (!allowApply) { toast.info('Your role can view swaps but not apply them.'); return; }
    const res = await httpPost(`/api/scheduler/rooming/swaps/${s.id}/apply`);
    if (res?.ok) {
      toast.success('Swap applied — patients notified');
      setAppliedIds((prev) => new Set([...prev, s.id]));
      refetch();
      onActionDone?.('apply', s);  // parent kanban refresh
    } else if (res?.error?.status === 404 || res?.error?.status === 501) {
      toast.info('Swap-apply endpoint not yet provisioned', {
        description: 'POST /api/scheduler/rooming/swaps/{id}/apply needs to ship to enable one-click apply.',
      });
    } else {
      toast.error('Apply failed', { description: res?.error?.message || 'Try again' });
    }
  };

  const dismissSwap = async (s) => {
    const res = await httpPost(`/api/scheduler/rooming/swaps/${s.id}/dismiss`);
    if (res?.ok) {
      toast.info('Swap dismissed');
      setDismissedIds((prev) => new Set([...prev, s.id]));
      onActionDone?.('dismiss', s);
    } else if (res?.error?.status === 404 || res?.error?.status === 501) {
      // Optimistic-dismiss when backend lacks the endpoint — keeps UI
      // responsive without false-positive errors.
      setDismissedIds((prev) => new Set([...prev, s.id]));
      onActionDone?.('dismiss', s);
    }
  };

  const visible = swaps.filter((s) => !dismissedIds.has(s.id));

  const roomingSource =
    loading ? 'loading'
    : apiMissing ? 'api_missing'
    : error ? 'error'
    : visible.length > 0 ? 'live'
    : 'empty';

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
      data-testid="real-rooming-orchestrator"
      data-rooming-source={roomingSource}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Rooming Orchestrator</p>
          <p className="text-[13px] font-semibold text-slate-800 mt-0.5" data-testid="real-orchestrator-count">
            {visible.length} suggested swap{visible.length === 1 ? '' : 's'}
          </p>
          <p className="text-[11px] text-slate-500">
            Echo re-plans the next 2 hours when a provider falls behind or a slot opens up.
          </p>
        </div>
        <Badge className="text-[9px] bg-slate-900 text-white border-0 gap-1">
          <Sparkles className="h-2.5 w-2.5" /> Live re-plan
        </Badge>
      </div>

      {apiMissing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2" data-testid="real-orchestrator-api-missing">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-amber-900">Swap suggestions API not yet provisioned</p>
            <ul className="text-[10px] text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
              <li><code>GET /api/scheduler/rooming/swaps?location_id=…&amp;date=…</code></li>
              <li><code>POST /api/scheduler/rooming/swaps/&#123;id&#125;/apply</code></li>
              <li><code>POST /api/scheduler/rooming/swaps/&#123;id&#125;/dismiss</code></li>
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-[11px] text-rose-700" data-testid="real-orchestrator-error">
          Failed to load swap suggestions: {error.message || 'unknown error'}
        </div>
      )}

      {!loading && !apiMissing && !error && visible.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center" data-testid="real-orchestrator-empty">
          <Sparkles className="h-4 w-4 text-slate-300 mx-auto mb-1" />
          <p className="text-[11px] text-slate-500">No swap suggestions right now — providers are on track.</p>
        </div>
      )}

      <div className="space-y-2">
        {visible.map((s) => {
          const impact = s.impact || {};
          const applied = appliedIds.has(s.id);
          return (
            <div
              key={s.id}
              className={`rounded-xl border p-3 space-y-2 ${applied ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`}
              data-testid={`real-orchestrator-row-${s.id}`}
            >
              <p className="text-[12px] font-semibold text-slate-800">{s.title || s.summary || 'Swap suggestion'}</p>
              <p className="text-[11px] text-slate-600">{s.proposal || s.description || '—'}</p>
              <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
                {(impact.patients_affected ?? impact.patientsAffected) != null && (
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-2.5 w-2.5" />
                    {impact.patients_affected ?? impact.patientsAffected} patient{(impact.patients_affected ?? impact.patientsAffected) === 1 ? '' : 's'}
                  </span>
                )}
                {(impact.time_recovered || impact.timeRecovered) && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />
                    {impact.time_recovered || impact.timeRecovered} recovered
                  </span>
                )}
                {(impact.revenue || impact.revenue_delta) && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                    {impact.revenue || impact.revenue_delta}
                  </span>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  className="h-7 text-[10px] bg-[#111] hover:bg-[#222] gap-1"
                  onClick={() => applySwap(s)}
                  disabled={applied || !allowApply}
                  data-testid={`real-orchestrator-apply-${s.id}`}
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {applied ? 'Applied' : 'One-click apply'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => toast.info('Preview coming soon')} data-testid={`real-orchestrator-preview-${s.id}`}>
                  Preview
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400" onClick={() => dismissSwap(s)} data-testid={`real-orchestrator-dismiss-${s.id}`}>
                  Dismiss
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RealRoomingOrchestrator;
